// /dealeri — pagina publică pentru dealeri, design conform spec strict:
// culori exact navy #0B1830, cream #F6F4EF, red #E14B3D, amber #D9A441; Georgia serif titluri;
// road-divider punctat; formular 3 pași cu bară de progres tip drum.
// Notă: hex hardcodate intenționat — user-mandated palette override peste sistemul semantic.

import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "@/lib/router-compat";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useDebounce } from "@/hooks/use-debounce";
import { DEALER_PLANS, type DealerPlanCode } from "@/components/dealers/DealerPlanSelector";
import { PENDING_KEY, type DealerSignupData } from "@/components/dealers/DealerCardStep";
import { Check } from "@phosphor-icons/react";
import InAppBrowserWarning from "@/components/layout/InAppBrowserWarning";
import { trackDealerSignupStep } from "@/lib/trackDealerSignupStep";

// ── Palette (Petrol & Bronz — aliniat cu design system global) ──
const C = {
  navy: "#0A1F1C",       // petrol deep
  navyLight: "#12332E",  // petrol surface
  cream: "#F4EFE6",      // warm off-white
  paper: "#F4EFE6",
  red: "#C9A46B",        // bronze (CTA principal)
  redDark: "#B08B54",    // bronze hover
  amber: "#C9A46B",      // bronze accent
  ink: "#0A1F1C",
  slate: "#5E5A4F",
} as const;

const DISPLAY = "'Instrument Serif', Georgia, 'Times New Roman', serif";
const BODY = "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
const MONO = "'Work Sans', 'SFMono-Regular', Consolas, monospace";

// ── Road divider (dotted amber) ──
function RoadDivider() {
  return (
    <div style={{ maxWidth: 1040, margin: "0 auto", padding: "24px 24px" }}>
      <div
        style={{
          height: 2,
          background: `repeating-linear-gradient(to right, ${C.amber} 0 14px, transparent 14px 28px)`,
        }}
      />
    </div>
  );
}

// ── Benefits data (exact text) ──
const BENEFITS = [
  ["Pagină proprie de dealer", "Subdomeniu alias.roautomarket.ro cu logo și descrierea firmei."],
  ["Panou cu statistici", "Vizualizări și mesaje per anunț, în timp real."],
  ["Vizibilitate pe toată platforma", "În rotația corectă din lista de rezultate."],
  ["Promovare cu reducere la volum", "Cost per anunț mai mic prin pachetele lunare."],
  ["Suport direct de la echipă", "Nu formular automat."],
];

// ── Plan card definitions (uses existing pricing) ──
type Plan = {
  code: DealerPlanCode | "pay_per_listing";
  name: string;
  price: string;
  cadence: string;
  label: string;
  featured?: boolean;
};

const PLANS: Plan[] = [
  { code: "start", name: "Start", price: "29,99 €", cadence: "pe lună", label: "Până la 5 anunțuri active" },
  { code: "standard", name: "Standard", price: "50 €", cadence: "pe lună", label: "Până la 60 de anunțuri active", featured: true },
  { code: "large", name: "Large", price: "120 €", cadence: "pe lună", label: "Anunțuri nelimitate" },
];

// ── Helpers ──
const stripAlias = (v: string) => v.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 40);
const fileToBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });

export default function DealeriNoi() {
  const [searchParams] = useSearchParams();
  const initialPlan: DealerPlanCode = (() => {
    const p = (searchParams.get("plan") || "").toLowerCase();
    return p === "start" || p === "standard" || p === "large" ? (p as DealerPlanCode) : "standard";
  })();
  const [totalActive, setTotalActive] = useState<number | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<DealerPlanCode>(initialPlan);
  const [payPerListing, setPayPerListing] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // Step 1
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyDescription, setCompanyDescription] = useState("");
  const [legalEntityType, setLegalEntityType] = useState("SRL");

  // Step 2
  const [showroomName, setShowroomName] = useState("");
  const [alias, setAlias] = useState("");
  const debouncedAlias = useDebounce(alias, 400);
  const [aliasState, setAliasState] = useState<"idle" | "checking" | "available" | "taken">("idle");
  const [address, setAddress] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [phone, setPhone] = useState("");

  // Step 3
  const [cui, setCui] = useState("");
  const [jNumber, setJNumber] = useState("");
  const [doc, setDoc] = useState<File | null>(null);

  // ── Log wizard open (funnel visibility) ──
  useEffect(() => {
    trackDealerSignupStep({ step: "wizard_opened" });
  }, []);

  // ── Fetch live listing count ──

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc("total_active_listings");
      if (cancelled) return;
      const n = Number(data);
      if (!error && Number.isFinite(n) && n > 0) setTotalActive(n);
      else if (error) console.warn("total_active_listings failed", error.message);
    })();
    return () => {
      cancelled = true;
    };
  }, []);


  // ── Auto-scroll to signup form when arriving with ?plan=… from /pricing ──
  useEffect(() => {
    if (!searchParams.get("plan")) return;
    const t = setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Alias uniqueness check (debounced) ──
  useEffect(() => {
    const a = debouncedAlias.trim();
    if (!a) {
      setAliasState("idle");
      return;
    }
    setAliasState("checking");
    supabase
      .rpc("is_subdomain_alias_available", { _alias: a })
      .then(({ data, error }) => {
        if (error) return setAliasState("idle");
        setAliasState(data ? "available" : "taken");
      });
  }, [debouncedAlias]);

  const canAdvanceStep2 = alias.length >= 3 && aliasState === "available";

  const scrollToForm = () => {
    document.getElementById("dealer-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const pickPlan = (code: DealerPlanCode | "pay_per_listing") => {
    if (code === "pay_per_listing") {
      setPayPerListing(true);
      setSelectedPlan("start"); // fallback for the form label
    } else {
      setPayPerListing(false);
      setSelectedPlan(code);
    }
    setTimeout(scrollToForm, 60);
  };

  const goNext = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRef.current) return;
    if (!formRef.current.reportValidity()) {
      trackDealerSignupStep({
        step: "validation_failed",
        email,
        errorField: `step_${step}`,
        errorMessage: "Campuri invalide (reportValidity)",
        metadata: { step },
      });
      return;
    }
    if (step === 2 && !canAdvanceStep2) {
      toast.error("Alege un alias disponibil înainte de a continua.");
      trackDealerSignupStep({
        step: "validation_failed",
        email,
        errorField: "alias",
        errorMessage: `Alias indisponibil (${aliasState})`,
      });
      return;
    }
    trackDealerSignupStep({
      step: step === 1 ? "step_1_completed" : "step_2_completed",
      email,
      metadata: { plan: payPerListing ? "pay_per_listing" : selectedPlan },
    });
    setStep((s) => (s < 3 ? ((s + 1) as 1 | 2 | 3) : s));
  };

  const goBack = () => setStep((s) => (s > 1 ? ((s - 1) as 1 | 2 | 3) : s));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    trackDealerSignupStep({ step: "submit_clicked", email });
    if (!formRef.current?.reportValidity()) {
      trackDealerSignupStep({
        step: "validation_failed",
        email,
        errorField: "step_3",
        errorMessage: "Campuri invalide la pasul 3",
      });
      return;
    }
    if (doc && doc.size > 10 * 1024 * 1024) {
      toast.error("Fișierul depășește 10 MB. Îl poți încărca ulterior din dashboard.");
      trackDealerSignupStep({
        step: "file_too_large",
        email,
        errorField: "document",
        errorMessage: `Fisier ${Math.round(doc.size / 1024 / 1024)} MB`,
      });
      return;
    }


    setSubmitting(true);
    try {
      // Documentul e opțional. Îl trecem prin sessionStorage doar dacă e mic
      // (base64 crește ~33%), altfel se încarcă ulterior din dashboard.
      let documentBase64: string | undefined;
      let documentFilename: string | undefined;
      if (doc && doc.size <= 1.5 * 1024 * 1024) {
        try {
          documentBase64 = await fileToBase64(doc);
          documentFilename = doc.name;
        } catch {
          documentBase64 = undefined;
          documentFilename = undefined;
        }
      }
      const planCode: DealerPlanCode = payPerListing ? "start" : selectedPlan;

      const payload: DealerSignupData = {
        email: email.trim().toLowerCase(),
        password,
        name: `${firstName.trim()} ${lastName.trim()}`.trim(),
        phone: phone.trim(),
        companyName: companyName.trim(),
        cui: cui.trim(),
        address: address.trim(),
        country: "RO",
        marketingConsent: false,
        planCode,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        legalEntityType,
        showroomName: showroomName.trim(),
        subdomainAlias: alias.trim().toLowerCase(),
        city: city.trim(),
        postalCode: postalCode.trim(),
        jNumber: jNumber.trim(),
        companyDescription: companyDescription.trim() || undefined,
        documentBase64,
        documentFilename,
      };

      try {
        sessionStorage.setItem(PENDING_KEY, JSON.stringify(payload));
      } catch {
        // Cota depășită (document mare) — salvăm fără document, se încarcă ulterior.
        sessionStorage.setItem(
          PENDING_KEY,
          JSON.stringify({ ...payload, documentBase64: undefined, documentFilename: undefined })
        );
      }


      const { data: res, error: invErr } = await supabase.functions.invoke(
        "create-signup-checkout-session",
        { body: { email: payload.email, returnOrigin: window.location.origin, plan_code: planCode } }
      );

      if (invErr || res?.error || !res?.url) {
        sessionStorage.removeItem(PENDING_KEY);
        if (res?.error === "email_exists") {
          toast.error("Există deja un cont cu acest email. Autentifică-te.");
        } else {
          toast.error(res?.error || invErr?.message || "Nu am putut porni plata.");
        }
        trackDealerSignupStep({
          step: "checkout_error",
          email: payload.email,
          errorField: "checkout",
          errorMessage: res?.error || invErr?.message || "Checkout indisponibil",
          metadata: { plan: planCode },
        });
        setSubmitting(false);
        return;
      }

      trackDealerSignupStep({
        step: "checkout_url_received",
        email: payload.email,
        metadata: { plan: planCode, hasDocument: Boolean(documentBase64) },
      });

      setSubmitted(true);
      // Brief confirmation before redirect
      setTimeout(() => {
        trackDealerSignupStep({ step: "checkout_redirect", email: payload.email });
        window.location.href = res.url;
      }, 900);

    } catch (err: any) {
      setSubmitting(false);
      toast.error(err?.message || "Eroare la trimitere.");
      trackDealerSignupStep({
        step: "error",
        email,
        errorField: "submit",
        errorMessage: err?.message || "Eroare necunoscuta la trimitere",
      });
    }
  };

  const selectedPlanName = useMemo(() => {
    if (payPerListing) return "Plată per anunț";
    return DEALER_PLANS.find((p) => p.code === selectedPlan)?.name || "Standard";
  }, [selectedPlan, payPerListing]);

  return (
    <div style={{ background: C.cream, color: C.ink, fontFamily: BODY, minHeight: "100vh" }}>
      <Helmet>
        <title>Cont de dealer pe RO Auto Market | Prețuri și înscriere</title>
        <meta
          name="description"
          content="Creează cont de dealer pe RO Auto Market. Publică anunțuri, gestionează stocul și primește contacte. Prețuri de la 29,99 €/lună."
        />
      </Helmet>

      {/* In-app browser blocker — Stripe checkout will fail inside Facebook/Instagram/TikTok WebView */}
      <InAppBrowserWarning blocking />

      {/* Sticky nav */}
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 40,
          background: C.navy,
          borderBottom: `1px solid ${C.navyLight}`,
          padding: "14px 20px",
        }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <a href="/" style={{ color: "#fff", fontFamily: DISPLAY, fontSize: 20, textDecoration: "none", fontWeight: 600 }}>
            RO Auto Market
          </a>
          <a
            href="#dealer-form"
            onClick={(e) => { e.preventDefault(); scrollToForm(); }}
            style={{
              background: C.red,
              color: C.ink,
              padding: "10px 18px",
              borderRadius: 999,
              textDecoration: "none",
              fontWeight: 600,
              fontSize: 14,
            }}
            onMouseOver={(e) => ((e.currentTarget.style.background = C.redDark))}
            onMouseOut={(e) => ((e.currentTarget.style.background = C.red))}
          >
            Creează cont dealer
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="surface-dark max-md:!pt-4 max-md:!pb-4" style={{ background: C.navy, color: "#fff", padding: "56px 20px 64px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", textAlign: "center" }}>
          <span
            className="max-md:!hidden"
            style={{
              display: "inline-block",
              border: `1px solid ${C.amber}`,
              color: C.amber,
              padding: "4px 14px",
              borderRadius: 999,
              fontSize: 12,
              fontFamily: MONO,
              letterSpacing: 0.5,
              marginBottom: 24,
            }}
          >
            Pentru dealeri auto
          </span>
          <h1
            className="max-md:!text-[24px] max-md:!mb-2"
            style={{
              fontFamily: DISPLAY,
              fontSize: "clamp(32px, 5vw, 52px)",
              lineHeight: 1.15,
              margin: "0 0 20px",
              fontWeight: 500,
            }}
          >
            Vinde mai mult, cu propriul tău colț pe RO Auto Market
          </h1>
          <p className="max-md:!text-[14px] max-md:!mb-3 max-md:!leading-snug" style={{ color: "#c9cfdc", fontSize: 17, lineHeight: 1.6, margin: "0 auto 28px", maxWidth: 560 }}>
            Firmă verificată, pagină proprie, statistici clare per anunț. Fără comision la vânzare, cost predictibil,
            suport direct de la echipa noastră.
          </p>
          <div className="max-md:!flex-nowrap max-md:!gap-2" style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <a
              className="max-md:!px-4 max-md:!py-2.5 max-md:!text-[14px] max-md:!whitespace-nowrap"
              href="#dealer-form"
              onClick={(e) => { e.preventDefault(); scrollToForm(); }}
              style={{
                background: C.red,
                color: C.ink,
                padding: "12px 24px",
                borderRadius: 999,
                textDecoration: "none",
                fontWeight: 600,
                fontSize: 15,
              }}
              onMouseOver={(e) => ((e.currentTarget.style.background = C.redDark))}
              onMouseOut={(e) => ((e.currentTarget.style.background = C.red))}
            >
              Creează cont dealer
            </a>
            <a
              className="max-md:!px-4 max-md:!py-2.5 max-md:!text-[14px] max-md:!whitespace-nowrap"
              href="#preturi"
              style={{
                border: "1px solid rgba(255,255,255,0.4)",
                color: "#fff",
                padding: "12px 24px",
                borderRadius: 999,
                textDecoration: "none",
                fontWeight: 600,
                fontSize: 15,
              }}
            >
              Vezi prețurile
            </a>
          </div>

          {/* Live stats */}
          <div
            className="max-md:!mt-3"
            style={{
              marginTop: 44,
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 16,
              maxWidth: 520,
              marginInline: "auto",
            }}
          >
            {[
               ...(totalActive !== null ? [{ v: totalActive.toLocaleString("ro-RO"), l: "vehicule listate" }] : []),
               { v: "0%", l: "comision la vânzare" },
            ].map((s, i) => (
              <div key={i}>
                <div style={{ fontFamily: MONO, fontSize: 22, color: C.amber, fontWeight: 700 }}>{s.v}</div>
                <div style={{ fontSize: 12, color: "#a8afbf", marginTop: 4 }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <RoadDivider />

      {/* Benefits */}
      <section style={{ padding: "24px 20px 48px" }}>
        <div style={{ maxWidth: 1040, margin: "0 auto" }}>
          <h2 style={{ fontFamily: DISPLAY, fontSize: "clamp(26px, 3.5vw, 36px)", textAlign: "center", margin: "0 0 36px", fontWeight: 500 }}>
            Ce primești ca dealer
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: 20,
            }}
          >
            {BENEFITS.map(([title, desc], i) => (
              <div
                key={i}
                style={{
                  background: C.paper,
                  borderRadius: 16,
                  padding: "22px 20px",
                  borderTop: `3px solid ${C.red}`,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                }}
              >
                <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 8px", color: C.ink, fontFamily: BODY }}>{title}</h3>
                <p style={{ fontSize: 14, color: C.slate, margin: 0, lineHeight: 1.55 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <RoadDivider />

      {/* Pricing */}
      <section id="preturi" style={{ padding: "24px 20px 48px" }}>
        <div style={{ maxWidth: 1040, margin: "0 auto" }}>
          <h2 style={{ fontFamily: DISPLAY, fontSize: "clamp(26px, 3.5vw, 36px)", textAlign: "center", margin: "0 0 12px", fontWeight: 500 }}>
            Alege cum plătești
          </h2>
          <p style={{ textAlign: "center", color: C.slate, margin: "0 auto 36px", maxWidth: 560 }}>
            Prețuri transparente. Fără comisioane pe tranzacție, fără cost per lead ascuns.
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: 20,
            }}
          >
            {PLANS.map((p) => {
              const isFeatured = !!p.featured;
              return (
                <div
                  key={p.code}
                  style={{
                    position: "relative",
                    background: isFeatured ? C.navy : C.paper,
                    color: isFeatured ? "#fff" : C.ink,
                    borderRadius: 16,
                    padding: "28px 22px 22px",
                    boxShadow: isFeatured ? "0 8px 24px rgba(11,24,48,0.15)" : "0 1px 3px rgba(0,0,0,0.04)",
                  }}
                >
                  {isFeatured && (
                    <span
                      style={{
                        position: "absolute",
                        top: -12,
                        left: "50%",
                        transform: "translateX(-50%)",
                        background: C.amber,
                        color: C.navy,
                        padding: "4px 14px",
                        borderRadius: 999,
                        fontSize: 11,
                        fontFamily: MONO,
                        fontWeight: 700,
                        letterSpacing: 0.6,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Cel mai popular
                    </span>
                  )}
                  <div style={{ fontFamily: DISPLAY, fontSize: 20, marginBottom: 8, fontWeight: 500 }}>{p.name}</div>
                  <div style={{ fontFamily: MONO, fontSize: 26, fontWeight: 700, color: isFeatured ? C.amber : C.ink }}>
                    {p.price}
                  </div>
                  <div style={{ fontSize: 12, color: isFeatured ? "#c9cfdc" : C.slate, marginBottom: 16 }}>
                    {p.cadence}
                  </div>
                  <div style={{ fontSize: 13, marginBottom: 20, lineHeight: 1.5, color: isFeatured ? "#e8ebf2" : C.ink }}>
                    {p.label}
                  </div>
                  <button
                    onClick={() => pickPlan(p.code)}
                    style={{
                      width: "100%",
                      background: isFeatured ? C.red : "transparent",
                      color: isFeatured ? C.ink : C.navy,
                      border: isFeatured ? "none" : `1px solid ${C.navy}`,
                      padding: "10px 16px",
                      borderRadius: 999,
                      cursor: "pointer",
                      fontSize: 14,
                      fontWeight: 600,
                      fontFamily: BODY,
                    }}
                  >
                    Alege {p.name}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <RoadDivider />

      {/* Signup form */}
      <section id="dealer-form" style={{ padding: "24px 20px 80px" }}>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <h2 style={{ fontFamily: DISPLAY, fontSize: "clamp(26px, 3.5vw, 36px)", textAlign: "center", margin: "0 0 8px", fontWeight: 500 }}>
            Creează cont de dealer
          </h2>
          <p style={{ textAlign: "center", color: C.slate, margin: "0 0 32px" }}>
            Plan ales: <strong style={{ color: C.ink }}>{selectedPlanName}</strong>
          </p>

          {submitted ? (
            <div style={{ background: C.paper, borderRadius: 16, padding: "40px 24px", textAlign: "center" }}>
              <div
                style={{
                  width: 56, height: 56, borderRadius: 999, background: C.red,
                  display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 16,
                }}
              >
                <Check size={28} color="#fff" weight="bold" />
              </div>
              <h3 style={{ fontFamily: DISPLAY, fontSize: 22, margin: "0 0 8px", fontWeight: 500 }}>
                Cont trimis spre verificare
              </h3>
              <p style={{ color: C.slate, margin: 0 }}>
                Te ducem la plata securizată. După confirmare, echipa noastră verifică documentul (de obicei în câteva ore).
              </p>
            </div>
          ) : (
            <>
              {/* Road progress bar */}
              <div style={{ position: "relative", padding: "8px 0 40px" }}>
                <div
                  style={{
                    position: "absolute",
                    top: 22, left: 24, right: 24, height: 2,
                    background: `repeating-linear-gradient(to right, ${C.amber} 0 8px, transparent 8px 16px)`,
                  }}
                />
                <div style={{ position: "relative", display: "flex", justifyContent: "space-between" }}>
                  {[
                    { n: 1, label: "Cont și firmă" },
                    { n: 2, label: "Locație" },
                    { n: 3, label: "Verificare" },
                  ].map((s) => {
                    const done = step > s.n;
                    const current = step === s.n;
                    return (
                      <div key={s.n} style={{ textAlign: "center", width: 120 }}>
                        <div
                          style={{
                            width: 36, height: 36, borderRadius: 999, margin: "0 auto",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            background: done || current ? C.red : C.paper,
                            color: done || current ? C.ink : C.slate,
                            border: done || current ? "none" : `1px solid #d5d7dc`,
                            fontFamily: MONO, fontSize: 14, fontWeight: 700,
                          }}
                        >
                          {done ? <Check size={16} weight="bold" /> : s.n}
                        </div>
                        <div
                          style={{
                            fontSize: 12, marginTop: 8,
                            color: current ? C.ink : C.slate,
                            fontWeight: current ? 700 : 400,
                          }}
                        >
                          {s.label}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <form
                ref={formRef}
                onSubmit={step === 3 ? handleSubmit : goNext}
                style={{ background: C.paper, borderRadius: 16, padding: "28px 24px" }}
              >
                {step === 1 && (
                  <div style={{ display: "grid", gap: 14 }}>
                    <Field label="Email" required>
                      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} />
                    </Field>
                    <Field label="Parolă (min 8 caractere)" required>
                      <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} style={inputStyle} />
                    </Field>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                      <Field label="Prenume" required>
                        <input type="text" required value={firstName} onChange={(e) => setFirstName(e.target.value)} style={inputStyle} />
                      </Field>
                      <Field label="Nume" required>
                        <input type="text" required value={lastName} onChange={(e) => setLastName(e.target.value)} style={inputStyle} />
                      </Field>
                    </div>
                    <Field label="Numele firmei" required>
                      <input type="text" required value={companyName} onChange={(e) => setCompanyName(e.target.value)} style={inputStyle} />
                    </Field>
                    <label style={{ display: "block" }}>
                      <span style={{ display: "block", fontSize: 13, color: C.ink, marginBottom: 6, fontWeight: 500 }}>
                        Descrierea firmei
                      </span>
                      <textarea
                        value={companyDescription}
                        onChange={(e) => setCompanyDescription(e.target.value)}
                        rows={4}
                        maxLength={1200}
                        placeholder="Câteva rânduri despre afacerea ta, vizibile pe pagina ta publică de dealer."
                        style={{ ...inputStyle, resize: "vertical", minHeight: 96, fontFamily: BODY, lineHeight: 1.5 }}
                      />
                      <div style={{ fontSize: 12, color: C.slate, marginTop: 6 }}>
                        Câteva rânduri despre afacerea ta, vizibile pe pagina ta publică de dealer. Dealerii cu descriere completă inspiră mai multă încredere.
                      </div>
                    </label>
                    <Field label="Formă de organizare" required>
                      <select required value={legalEntityType} onChange={(e) => setLegalEntityType(e.target.value)} style={inputStyle}>
                        <option value="SRL">SRL</option>
                        <option value="PFA">PFA</option>
                        <option value="SA">SA</option>
                      </select>
                    </Field>
                  </div>
                )}

                {step === 2 && (
                  <div style={{ display: "grid", gap: 14 }}>
                    <Field label="Denumire showroom sau parc" required>
                      <input type="text" required value={showroomName} onChange={(e) => setShowroomName(e.target.value)} style={inputStyle} />
                    </Field>
                    <Field label="Alias subdomeniu" required>
                      <div style={{ display: "flex", alignItems: "stretch", border: "1px solid #d5d7dc", borderRadius: 10, overflow: "hidden" }}>
                        <input
                          type="text"
                          required
                          minLength={3}
                          value={alias}
                          onChange={(e) => setAlias(stripAlias(e.target.value))}
                          placeholder="autohouse"
                          style={{ ...inputStyle, border: "none", borderRadius: 0, flex: 1 }}
                        />
                        <span style={{ padding: "10px 14px", background: "#f0eee9", color: C.slate, fontFamily: MONO, fontSize: 13, alignSelf: "center" }}>
                          .roautomarket.ro
                        </span>
                      </div>
                      <div style={{ fontSize: 12, marginTop: 6, minHeight: 16 }}>
                        {alias.length > 0 && alias.length < 3 && <span style={{ color: C.slate }}>Minim 3 caractere.</span>}
                        {alias.length >= 3 && aliasState === "checking" && <span style={{ color: C.slate }}>Se verifică...</span>}
                        {alias.length >= 3 && aliasState === "available" && <span style={{ color: "#2c8a5a" }}>✓ Disponibil</span>}
                        {alias.length >= 3 && aliasState === "taken" && <span style={{ color: "#8A2A1B" }}>Acest alias e deja folosit, alege altul.</span>}
                      </div>
                    </Field>
                    <Field label="Adresă" required>
                      <input type="text" required value={address} onChange={(e) => setAddress(e.target.value)} style={inputStyle} />
                    </Field>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 14 }}>
                      <Field label="Cod poștal" required>
                        <input type="text" required value={postalCode} onChange={(e) => setPostalCode(e.target.value)} style={inputStyle} />
                      </Field>
                      <Field label="Oraș" required>
                        <input type="text" required value={city} onChange={(e) => setCity(e.target.value)} style={inputStyle} />
                      </Field>
                    </div>
                    <Field label="Telefon" required>
                      <input type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)} style={inputStyle} />
                    </Field>
                  </div>
                )}

                {step === 3 && (
                  <div style={{ display: "grid", gap: 14 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                      <Field label="CUI" required>
                        <input type="text" required value={cui} onChange={(e) => setCui(e.target.value)} style={inputStyle} placeholder="RO12345678" />
                      </Field>
                      <Field label="Nr. înregistrare J" required>
                        <input type="text" required value={jNumber} onChange={(e) => setJNumber(e.target.value)} style={inputStyle} placeholder="J40/1234/2020" />
                      </Field>
                    </div>
                    <label
                      style={{
                        display: "block",
                        border: `2px dashed ${C.red}`,
                        borderRadius: 12,
                        padding: "28px 20px",
                        textAlign: "center",
                        cursor: "pointer",
                        background: doc ? "#fdf2f0" : "transparent",
                      }}
                    >
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={(e) => setDoc(e.target.files?.[0] || null)}
                        style={{ display: "none" }}
                      />
                      <div style={{ fontWeight: 600, color: C.ink, marginBottom: 4 }}>
                        {doc ? doc.name : "Adaugă fișier (opțional, maxim 10 MB)"}
                      </div>
                      <div style={{ fontSize: 12, color: C.slate }}>
                        Imagine sau PDF cu certificatul de înregistrare (CUI + J).
                      </div>
                    </label>
                    <p style={{ fontSize: 12, color: C.slate, margin: 0 }}>
                      Documentul este opțional. Firmă verificată la ANAF și RISCO; îl poți încărca oricând din dashboard, iar publicarea anunțurilor pornește imediat după plată.
                    </p>

                  </div>
                )}

                {/* Nav */}
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 28, gap: 12 }}>
                  <button
                    type="button"
                    onClick={goBack}
                    disabled={step === 1}
                    style={{
                      ...ghostBtn,
                      opacity: step === 1 ? 0.4 : 1,
                      cursor: step === 1 ? "not-allowed" : "pointer",
                    }}
                  >
                    Înapoi
                  </button>
                  {step < 3 ? (
                    <button
                      type="submit"
                      disabled={step === 2 && !canAdvanceStep2}
                      style={{
                        ...primaryBtn,
                        opacity: step === 2 && !canAdvanceStep2 ? 0.5 : 1,
                        cursor: step === 2 && !canAdvanceStep2 ? "not-allowed" : "pointer",
                      }}
                    >
                      Continuă
                    </button>
                  ) : (
                    <button type="submit" disabled={submitting} style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}>
                      {submitting ? "Se pregătește..." : "Creează cont dealer"}
                    </button>
                  )}
                </div>
              </form>
            </>
          )}
        </div>
      </section>

    </div>
  );
}

// ── Small helpers ──
function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label style={{ display: "block" }}>
      <span style={{ display: "block", fontSize: 13, color: "#171712", marginBottom: 6, fontWeight: 500 }}>
        {label}{required && <span style={{ color: "#E14B3D" }}> *</span>}
      </span>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 14px",
  border: "1px solid #d5d7dc",
  borderRadius: 10,
  fontSize: 15,
  fontFamily: BODY,
  background: "#fff",
  color: C.ink,
  outline: "none",
};

const primaryBtn: React.CSSProperties = {
  background: C.red,
  color: C.ink,
  border: "none",
  padding: "12px 26px",
  borderRadius: 999,
  fontWeight: 600,
  fontSize: 15,
  fontFamily: BODY,
  cursor: "pointer",
};

const ghostBtn: React.CSSProperties = {
  background: "transparent",
  color: C.navy,
  border: `1px solid ${C.navy}`,
  padding: "12px 26px",
  borderRadius: 999,
  fontWeight: 600,
  fontSize: 15,
  fontFamily: BODY,
  cursor: "pointer",
};
